import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { SWAL_DEFAULTS } from '../core/constants';
import { UiHelperService } from './ui-helper.service';

/** Opciones base para SweetAlert2 que previenen bloqueo de la interfaz */
export interface SwalBaseConfig {
  allowOutsideClick: boolean;
  allowEscapeKey: boolean;
  allowEnterKey: boolean;
  focusConfirm: boolean;
  backdrop: boolean;
  willOpen?: () => void;
  didClose?: () => void;
  didDestroy?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  constructor(private uiHelper: UiHelperService) {}

  /**
   * Restaura clics y scroll: Swal huérfano + spinner/backdrop de Material.
   * No toca un Swal que sigue visible (evita matar el siguiente diálogo en cadena).
   */
  cleanupSwal(): void {
    // Si hay un Swal abierto (p. ej. error justo después del confirm), solo quita
    // overlays de carga CDK; no borrar .swal2-container ni clases de Swal.
    if (Swal.isVisible()) {
      this.uiHelper.stripLoadingOverlay();
      this.forceUnlockCdk();
      return;
    }

    this.uiHelper.unlockUi();

    document.body.classList.remove(
      'swal2-shown',
      'swal2-height-auto',
      'swal2-backdrop-show',
      'swal2-no-backdrop',
      'swal2-toast-shown'
    );
    document.documentElement.classList.remove(
      'swal2-shown',
      'swal2-height-auto',
      'swal2-backdrop-show',
      'loading-open'
    );
    document.body.classList.remove('loading-open');

    document.querySelectorAll('.swal2-container').forEach((el) => el.remove());

    this.uiHelper.stripLoadingOverlay();
    this.forceUnlockCdk();
    // Swal oculta app-root con aria-hidden; al remover el contenedor a mano hay que restaurarlo.
    this.uiHelper.restoreShellAriaHidden();
  }

  /** Fuerza desbloqueo aunque queden otros panes (tour, tooltips, etc.). */
  private forceUnlockCdk(): void {
    document.documentElement.classList.remove('cdk-global-scrollblock');
    document.body.classList.remove('cdk-global-scrollblock');

    // Backdrops de loading o sin pane: siempre quitar.
    document.querySelectorAll('.cdk-overlay-backdrop').forEach((el) => {
      const isLoading = el.classList.contains('loading-backdrop');
      const parent = el.parentElement;
      const hasPane = !!parent?.querySelector('.cdk-overlay-pane');
      if (isLoading || !hasPane) {
        el.remove();
      }
    });
  }

  /**
   * Retorna la configuración base de Swal para reutilizar en todos los fire().
   */
  getSwalBaseConfig(): SwalBaseConfig {
    return {
      allowOutsideClick: true,
      allowEscapeKey: true,
      allowEnterKey: true,
      focusConfirm: false,
      backdrop: true,
      willOpen: () => this.uiHelper.unlockUi(),
      didClose: () => {
        // Un solo pase diferido: los setTimeout(50/200) anteriores borraban el
        // siguiente Swal (confirm → error/revocar) y dejaban la UI congelada.
        queueMicrotask(() => this.cleanupSwal());
      },
      didDestroy: () => {
        queueMicrotask(() => this.cleanupSwal());
      }
    };
  }

  /**
   * Base Swal cuyo `didClose` ejecuta primero la limpieza de `getSwalBaseConfig()` y luego `after`.
   */
  getSwalBaseConfigWithAfterClose(after: () => void): SwalBaseConfig {
    const base = this.getSwalBaseConfig();
    const previous = base.didClose;
    return {
      ...base,
      didClose: () => {
        previous?.();
        after();
      }
    };
  }

  /** Variante habitual: limpiar overlays y recargar la página al cerrar el Swal. */
  getSwalBaseConfigReloadAfterClose(): SwalBaseConfig {
    return this.getSwalBaseConfigWithAfterClose(() => window.location.reload());
  }

  /** Texto por defecto del botón de confirmación */
  get swalConfirmButtonText(): string {
    return SWAL_DEFAULTS.CONFIRM_BUTTON_TEXT;
  }

  /** Clases CSS personalizadas para Swal */
  get swalCustomClass(): typeof SWAL_DEFAULTS.CUSTOM_CLASS {
    return { ...SWAL_DEFAULTS.CUSTOM_CLASS };
  }

  /** Toast de éxito al descargar documento (verificar / firmar). */
  showDownloadSuccessToast(title: string = 'Documento descargado'): void {
    Swal.fire({
      toast: true,
      position: 'bottom',
      icon: 'success',
      title,
      showConfirmButton: false,
      timer: 2800,
      timerProgressBar: true,
      customClass: {
        popup: 'swal-toast-download-success',
        title: 'swal-toast-download-title',
        timerProgressBar: 'swal-toast-download-progress'
      },
      didClose: () => {
        setTimeout(() => this.cleanupSwal(), 80);
      }
    });
  }
}
